import { h } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { tagParser, linkParser, isTouchDevice } from '../../js/modules/utilities.js'
import { attributesAnalytics } from '../../js/modules/component'

export const Campagne = ({ alt, analytics, background = 'black', cacheKey, id, image, image_mobile, legalNotice, target, URL, video }) => {
   const [videoSource, setVideoSource] = useState(window.innerWidth >= 736 ? video.url : video.url_mobile)
   const [imageFallback, setImageFallback] = useState(window.innerWidth >= 736 ? image : image_mobile)
   const [isPlaying, setIsPlaying] = useState(true)
   const [hasEnded, setHasEnded] = useState(false)
   const [isMuted, setIsMuted] = useState(true)
   const [isFallback, setIsFallback] = useState(false)
   const videoRef = useRef(null)

   useEffect(() => {
      // Cookie de capping
      updateCookieCampaign(id)

      // Image Fallback
      const setTimeoutId = setTimeout(() => {
         if (videoRef.current.currentTime > 0) return // Protection: En loop, l'event playing ne se déclenche pas
         videoRef.current.src = '' // Evite au navigateur de continuer à télécharger la vidéo
         setIsFallback(true)
      }, 5000)
      const handlePlaying = () => {
         clearTimeout(setTimeoutId)
      }
      videoRef.current.addEventListener('playing', handlePlaying)

      // Page resize
      const handleResize = () => {
         setVideoSource(window.innerWidth >= 736 ? video.url : video.url_mobile)
         setImageFallback(window.innerWidth >= 736 ? image : image_mobile)
         videoRef.current.load() // Reload the video with the new source
         setHasEnded(false)
         setIsPlaying(true)
      }
      window.addEventListener('resize', handleResize)

      // Cleanup
      return () => {
         window.removeEventListener('resize', handleResize)
         if (videoRef.current) {
            videoRef.current.removeEventListener('playing', handlePlaying)
         }
      }
   }, [])

   return (
      <div class={`campagne hasBg-${background}`} data-cache-key={cacheKey}>
         <a href={URL} aria-label={alt} target={target} {...attributesAnalytics(analytics)}>
            {isFallback ? (
               <img class="fallback-img" src={imageFallback} alt="" />
            ) : (
               <video
                  aria-label=""
                  ref={videoRef}
                  class="video"
                  autoplay={video.autoplay}
                  loop={video.loop}
                  muted
                  preload="auto"
                  tabIndex="-1"
                  playsinline // mobile: evite le fullscreen
               >
                  <source src={videoSource} type="video/mp4" />
               </video>
            )}
         </a>

         {legalNotice && typeof legalNotice === 'object' && <LegalNotice {...legalNotice} background={background} />}

         {!isFallback && (
            <VideoControls
               video={{
                  ref: videoRef,
                  hasSound: !video.mute,
                  isMuted: isMuted,
                  setIsMuted: setIsMuted,
                  isPlaying: isPlaying,
                  setIsPlaying: setIsPlaying,
                  hasEnded: hasEnded,
                  setHasEnded: setHasEnded,
               }}
               background={background}
            />
         )}
      </div>
   )
}

// VIDEO CONTROLS =============================================
const VideoControls = ({ video, background }) => {
   // icons/labels
   const iconPlay = (
      <svg width="18" height="20" viewBox="0 0 18 20" xmlns="http://www.w3.org/2000/svg">
         <path d="M8.94821 14.7252L0 19.4253V0.625L8.94821 5.32507L17.6253 10.0251L8.94821 14.7252Z" />
      </svg>
   )
   const iconPlayLabel = 'Lecture de la vidéo'
   const iconPause = (
      <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
         <path d="M7.27511 17.6253H0.375V0.375H7.27511V17.6253ZM17.6253 17.6253H10.7252V0.375H17.6253V17.6253Z" />
      </svg>
   )
   const iconPauseLabel = 'Pause de la vidéo'
   const iconReplay = (
      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
         <path d="M13.5 2c-5.621 0-10.211 4.443-10.475 10h-3.025l5 6.625 5-6.625h-2.975c.257-3.351 3.06-6 6.475-6 3.584 0 6.5 2.916 6.5 6.5s-2.916 6.5-6.5 6.5c-1.863 0-3.542-.793-4.728-2.053l-2.427 3.216c1.877 1.754 4.389 2.837 7.155 2.837 5.79 0 10.5-4.71 10.5-10.5s-4.71-10.5-10.5-10.5z" />
      </svg>
   )
   const iconReplayLabel = 'Rejouer la vidéo'
   const iconMuted = (
      <svg width="24" height="24" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
         <path d="M30 19.348v2.652h-2.652l-3.348-3.348-3.348 3.348h-2.652v-2.652l3.348-3.348-3.348-3.348v-2.652h2.652l3.348 3.348 3.348-3.348h2.652v2.652l-3.348 3.348 3.348 3.348z"></path>
         <path d="M13 30c-0.26 0-0.516-0.102-0.707-0.293l-7.707-7.707h-3.586c-0.552 0-1-0.448-1-1v-10c0-0.552 0.448-1 1-1h3.586l7.707-7.707c0.286-0.286 0.716-0.372 1.090-0.217s0.617 0.519 0.617 0.924v26c0 0.404-0.244 0.769-0.617 0.924-0.124 0.051-0.254 0.076-0.383 0.076z"></path>
      </svg>
   )
   const iconMutedLabel = 'Activer le son de la vidéo'
   const iconUnmuted = (
      <svg width="24" height="24" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
         <path d="M22.485 25.985c-0.384 0-0.768-0.146-1.061-0.439-0.586-0.586-0.586-1.535 0-2.121 4.094-4.094 4.094-10.755 0-14.849-0.586-0.586-0.586-1.536 0-2.121s1.536-0.586 2.121 0c2.55 2.55 3.954 5.94 3.954 9.546s-1.404 6.996-3.954 9.546c-0.293 0.293-0.677 0.439-1.061 0.439v0zM17.157 23.157c-0.384 0-0.768-0.146-1.061-0.439-0.586-0.586-0.586-1.535 0-2.121 2.534-2.534 2.534-6.658 0-9.192-0.586-0.586-0.586-1.536 0-2.121s1.535-0.586 2.121 0c3.704 3.704 3.704 9.731 0 13.435-0.293 0.293-0.677 0.439-1.061 0.439z"></path>
         <path d="M13 30c-0.26 0-0.516-0.102-0.707-0.293l-7.707-7.707h-3.586c-0.552 0-1-0.448-1-1v-10c0-0.552 0.448-1 1-1h3.586l7.707-7.707c0.286-0.286 0.716-0.372 1.090-0.217s0.617 0.519 0.617 0.924v26c0 0.404-0.244 0.769-0.617 0.924-0.124 0.051-0.254 0.076-0.383 0.076z"></path>
      </svg>
   )
   const iconUnmutedLabel = 'Désactiver le son de la vidéo'

   // play/pause
   const handlePlayPause = (e) => {
      e.preventDefault() // evite le clic sur le lien
      e.stopPropagation() // evite l'envoi du taggage
      if (video.isPlaying) {
         video.ref.current.pause()
      } else {
         video.ref.current.play()
      }
      video.setHasEnded(false)
      video.setIsPlaying(!video.isPlaying)
   }

   // video ended
   useEffect(() => {
      const handleVideoEnded = () => {
         video.setIsPlaying(false)
         video.setHasEnded(true)
      }
      video.ref.current.addEventListener('ended', handleVideoEnded)

      return () => {
         if (video.ref.current) {
            video.ref.current.removeEventListener('ended', handleVideoEnded)
         }
      }
   }, [])

   // mute / unmute
   const handleMuteUnmute = (e) => {
      e.preventDefault() // evite le clic sur le lien
      e.stopPropagation() // evite l'envoi du taggage
      video.ref.current.muted = !video.isMuted
      video.setIsMuted(!video.isMuted)
   }

   return (
      <div class={`video-controls hasBg-${background}`}>
         {video.hasSound && (
            <button class="control-btn btn" onClick={handleMuteUnmute} aria-label={video.isMuted ? iconMutedLabel : iconUnmutedLabel}>
               {video.isMuted ? iconMuted : iconUnmuted}
            </button>
         )}
         <button class="control-btn btn" onClick={handlePlayPause} aria-label={video.isPlaying ? iconPauseLabel : video.hasEnded ? iconReplayLabel : iconPlayLabel}>
            {video.isPlaying ? iconPause : video.hasEnded ? iconReplay : iconPlay}
         </button>
      </div>
   )
}

// MENTIONS LEGALES ===========================================
const LegalNotice = ({ background, content, das, showButtonAriaLabel, showButtonLabel, title }) => {
   const [legalState, setLegalState] = useState(false)
   const [legalHoverState, setLegalHoverState] = useState(false)

   // Close legal notice on Escape key when panel is open
   useEffect(() => {
      if (!legalState) return
      const onKeyDown = (e) => {
         const key = e.key || e.code
         if (key === 'Escape' || key === 'Esc' || e.keyCode === 27) {
            e.stopPropagation()
            setLegalState(false)
         }
      }
      document.addEventListener('keydown', onKeyDown)
      return () => document.removeEventListener('keydown', onKeyDown)
   }, [legalState])

   const toggleLegals = (e) => {
      e.preventDefault() // evite le clic sur le lien
      e.stopPropagation() // evite l'envoi du taggage
      setLegalState(!legalState)
   }

   const showLegals = () => {
      if (!isTouchDevice()) setLegalHoverState(true)
   }

   const hideLegals = () => {
      if (!isTouchDevice()) setLegalHoverState(false)
   }

   return (
      <div
         class={`legalNoticeWrapper ${legalState ? 'openContent' : ''} ${legalHoverState ? 'isHover' : ''} ${background === 'white' ? 'grey-bg' : 'black-bg'}`}
         onMouseLeave={hideLegals}
      >
         <button
            class="showButton btn"
            onClick={toggleLegals}
            onMouseEnter={showLegals}
            tabindex="0"
            role="button"
            aria-expanded={legalState}
            aria-pressed={legalState}
            aria-controls="legalNoticePanelCampagne"
            aria-label={showButtonAriaLabel}
         >
            <span className="icon icon-info"></span>&nbsp;<span className="showButton-label">{showButtonLabel}</span>
         </button>
         <div id="legalNoticePanelCampagne" class="contentLegalNotice" aria-hidden={!legalState}>
            <span class="closeLegal" onClick={toggleLegals}>
               &times;
            </span>
            <div class="contentText">
               {das && <strong class="legalNoticeTitle standard pb-0">DAS :</strong>}
               {das && <p dangerouslySetInnerHTML={{ __html: tagParser(das) }}></p>}
               {title && <strong class="legalNoticeTitle standard pb-0" dangerouslySetInnerHTML={{ __html: tagParser(title) }}></strong>}
               {content && <p dangerouslySetInnerHTML={{ __html: tagParser(linkParser(content)) }}></p>}
            </div>
         </div>
      </div>
   )
}

/**
 * Met à jour le cookie de campagne avec l'ID de la campagne spécifiée.
 * Si le cookie existe déjà, il incrémente la valeur associée à l'ID de la campagne.
 * Si le cookie n'existe pas, il crée un nouveau cookie avec la valeur initiale de 1 pour l'ID de la campagne.
 *
 * @param {string} idCampagne - L'ID de la campagne à mettre à jour dans le cookie.
 * @param {Date} [dateExpiration=new Date(new Date().setMonth(new Date().getMonth() + 1))] - La date d'expiration du cookie. Le format de la date doit être un objet Date valide.
 */
const updateCookieCampaign = (idCampagne, dateExpiration = new Date(new Date().setMonth(new Date().getMonth() + 1))) => {
   const cookieName = 'o-HPevent'
   let cookieValue = {
      [idCampagne]: 1,
   }
   let eventCookie = document.cookie.split('; ').find((cookie) => cookie.includes(cookieName))
   if (eventCookie) {
      try {
         let eventCookieValue = JSON.parse(eventCookie.split('=')[1])
         const currentDisplayValue = parseInt(eventCookieValue[idCampagne], 10) || 0
         cookieValue[idCampagne] = currentDisplayValue + 1
      } catch (e) {}
   }
   document.cookie = `${cookieName}=${JSON.stringify(cookieValue)}; expires=${dateExpiration.toUTCString()}; path=/; domain=.orange.fr`
}
